import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo } from 'react';

dayjs.extend(utc);
dayjs.extend(timezone);
import { useSearchParams } from 'react-router-dom';

import {
  allFilters,
  DateRangeFilter,
  determineCorrectDateFromPresetOrRange,
  RangePickerQueryParamKeys,
  TimeRangeConstraints,
} from '@cast/shared-date-ui';

export const INVENTORY_WORKLOADS_QUERY_PARAM_KEYS: RangePickerQueryParamKeys = {
  fromDate: 'inventory_workloads_chart_from',
  toDate: 'inventory_workloads_chart_to',
  dateRangePreset: 'inventory_workloads_chart_date_preset',
  timeZone: 'inventory_workloads_chart_timezone',
};

export const INVENTORY_WORKLOADS_FILTER_PRESETS = [
  DateRangeFilter.LastHour,
  DateRangeFilter.Last24Hours,
  DateRangeFilter.Last7Days,
  DateRangeFilter.Last30Days,
];

const INITIAL_PRESET = DateRangeFilter.Last7Days;

const CONSTRAINTS: TimeRangeConstraints = {
  minDate: ({ timezone }) =>
    dayjs().tz(timezone).subtract(30, 'days').startOf('day').toISOString(),
};

export const useInventoryWorkloadsTimeRangeSelection = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryParamKeys = INVENTORY_WORKLOADS_QUERY_PARAM_KEYS;

  const rawFrom = searchParams.get(queryParamKeys.fromDate) || undefined;
  const rawTo = searchParams.get(queryParamKeys.toDate) || undefined;
  const preset = searchParams.get(queryParamKeys.dateRangePreset) || undefined;
  const timeZone =
    searchParams.get(queryParamKeys.timeZone) || dayjs.tz.guess();

  const getRange = useCallback(() => {
    return determineCorrectDateFromPresetOrRange({
      preset: preset as DateRangeFilter | undefined,
      timezone: timeZone,
      from: rawFrom,
      to: rawTo,
      fallbackPreset: INITIAL_PRESET,
      allowTimeInput: true,
      constraints: CONSTRAINTS,
    });
  }, [preset, rawFrom, rawTo, timeZone]);

  const key = `${rawFrom}-${rawTo}-${preset}-${timeZone}`;

  const range = useMemo(() => getRange(), [key, getRange]);

  useEffect(() => {
    const hasRange = rawFrom && rawTo;
    const hasPreset = preset && preset in allFilters;

    if (!hasRange && !hasPreset) {
      setSearchParams(
        (prev) => {
          prev.set(queryParamKeys.fromDate, range.from);
          prev.set(queryParamKeys.toDate, range.to);
          prev.set(queryParamKeys.timeZone, timeZone);

          return prev;
        },
        { replace: true }
      );
    }
  }, [
    rawFrom,
    rawTo,
    preset,
    timeZone,
    range,
    queryParamKeys,
    setSearchParams,
  ]);

  useEffect(() => {
    if (preset) {
      const presetFilter = allFilters[preset as DateRangeFilter];
      if (!presetFilter) {
        setSearchParams(
          (prev) => {
            prev.delete(queryParamKeys.dateRangePreset);
            return prev;
          },
          { replace: true }
        );
      }
    }
  }, [preset, queryParamKeys.dateRangePreset, setSearchParams]);

  return {
    from: range.from,
    to: range.to,
    preset: preset as DateRangeFilter | undefined,
    timeZone,
    getRange,
    key,
    initialState: {
      preset: INITIAL_PRESET,
      timeZone,
    },
    constraints: CONSTRAINTS,
  };
};
